/**
 * Kameleoon Experimentation Platform Integration
 *
 * Handles initialization and page view tracking for Kameleoon A/B testing platform.
 * This module integrates with the Consent Manager Platform (CMP) to ensure GDPR compliance
 * and provides proper SPA (Single Page Application) support via page view events.
 *
 * Key Features:
 * - Consent-gated initialization (only runs after CMP consent granted)
 * - SPA support with page view event tracking on route changes
 * - Protection against double initialization
 * - Retry mechanism for delayed consent scenarios
 * - GDPR-compliant consent revocation handling
 *
 * Flow:
 * 1. User grants consent via CMP
 * 2. CMP loads Kameleoon script (changes type="text/plain" to type="text/javascript")
 * 3. Kameleoon SDK loads and becomes available at window.Kameleoon
 * 4. CMP calls initKameleoon function (via consentManager.ts)
 * 5. We enable legal consent via enableLegalConsent()
 * 6. We enable SPA support via enableSinglePageSupport()
 * 7. Initial page_view event is sent
 * 8. Subsequent page_view events are triggered by onRouteUpdate on each route change
 *
 * Edge Cases Handled:
 * 1. Initial load with consent:
 *    → SDK loads → initKameleoon() → enableLegalConsent() → page_view ✓
 *
 * 2. Initial load without consent:
 *    → Script blocked by CMP → SDK never loads → No tracking ✓
 *
 * 3. User accepts consent after page load (delayed consent):
 *    → CMP loads script → initKameleoon() called but SDK not ready yet
 *    → Retry mechanism (50 attempts, 100ms delay, max 5 seconds)
 *    → Once SDK loads → initialization completes ✓
 *
 * 4. User revokes consent after initialization:
 *    → CMP triggers consent event → disableKameleoon() called
 *    → disableLegalConsent() stops data collection
 *    → kameleoonInitialized flag reset → No more page_view events ✓
 *
 * 5. Double initialization attempt:
 *    → kameleoonInitialized flag prevents re-initialization ✓
 *
 * 6. SDK fails to load (network error, timeout):
 *    → Retry mechanism exhausted after 5 seconds
 *    → Graceful failure with error log → No crash ✓
 *
 * 7. API method errors (enableLegalConsent, enableSinglePageSupport):
 *    → Try-catch blocks handle errors
 *    → Error logged to console → kameleoonInitialized remains false ✓
 *
 * 8. SPA navigation before initialization:
 *    → sendKameleoonPageView() checks kameleoonInitialized flag
 *    → Returns early without sending event → No errors ✓
 *
 * GDPR Compliance:
 * - Triple-layer protection:
 *   1. CMP blocks script loading until consent (gatsby-ssr.tsx)
 *   2. Kameleoon dashboard "Consent required" setting
 *   3. Explicit enableLegalConsent() / disableLegalConsent() calls in code
 *
 * Integration Points:
 * - gatsby-ssr.tsx: Script tag injection with CMP consent attributes
 * - gatsby-browser.js: Initialization via initConsentManager() and route tracking via onRouteUpdate()
 * - consentManager.ts: Vendor consent check before initialization
 *
 * @see https://developers.kameleoon.com/apis/activation-api-js/api-reference/
 * @see https://help.kameleoon.com/experimentation/web-experimentation/advanced-experiment-types/set-up-experiment-single-page-app/
 */

import log from "~/lib/log"

/**
 * TypeScript declarations for Kameleoon SDK global API
 *
 * The Kameleoon SDK is loaded via a script tag and exposes a global `window.Kameleoon` object.
 * This interface defines the API methods we use for initialization and event tracking.
 */
export type KameleoonVariation = {
  id: number
  name: string
}

export type KameleoonExperiment = {
  id: number
  name: string
  active: boolean
  associatedVariation: KameleoonVariation | null
}

declare global {
  interface Window {
    Kameleoon?: {
      API: {
        // Core SDK functionality
        Core: {
          // Enable Single Page Application support for route change tracking
          enableSinglePageSupport: () => void
          // Enable legal consent (GDPR) - allows data collection
          enableLegalConsent: (type?: string) => void
          // Disable legal consent (GDPR) - stops data collection
          disableLegalConsent: (type?: string) => void
          // Get current configuration
          getConfiguration: () => unknown
        }
        // Event tracking for SPA navigation
        Events: {
          // Trigger custom events (e.g., 'page_view' for route changes)
          trigger: (eventName: string) => void
        }
        // Experiment access
        Experiments: {
          getById: (id: number) => KameleoonExperiment | undefined
          getActive: () => KameleoonExperiment[]
          getActivatedInVisit: () => KameleoonExperiment[]
        }
      }
    }
  }
}

/**
 * Flag to track if Kameleoon SDK has been initialized
 *
 * Prevents double initialization which could cause:
 * - Duplicate event tracking
 * - Performance issues
 * - Incorrect experiment evaluation
 */
let kameleoonInitialized = false

/**
 * Function to send a page view event to Kameleoon for SPA navigation
 *
 * In Single Page Applications (SPAs), the page doesn't fully reload on navigation.
 * This function tells Kameleoon to re-evaluate experiments for the current page.
 */
export function sendKameleoonPageView(): void {
  // Safety check: Only send page_view if SDK is initialized
  // Initial page_view is sent by initKameleoon(), this handles SPA navigation only
  if (!window.Kameleoon?.API?.Events?.trigger || !kameleoonInitialized) {
    return
  }

  // Trigger page view event for SPA navigation
  // This tells Kameleoon: "User navigated to a new page, re-evaluate which experiments to show"
  window.Kameleoon.API.Events.trigger("page_view")
  log("page_view event triggered", "kameleoon")
}

/**
 * Initializes Kameleoon SDK after consent has been granted
 *
 * @param retryCount - Internal counter for retry attempts (used for delayed consent scenario)
 */
export function initKameleoon(retryCount = 0): void {
  const MAX_RETRIES = 50 // 50 * 100ms = 5 seconds max wait time
  const RETRY_DELAY = 100 // milliseconds between retries

  // Prevent double initialization
  if (kameleoonInitialized) {
    log("already initialized", "kameleoon")
    return
  }

  // Check if SDK is loaded (script may not have loaded yet or failed to load)
  if (!window.Kameleoon) {
    // If user accepts consent after page load, script needs time to load
    // Retry with fixed delay up to MAX_RETRIES times
    if (retryCount < MAX_RETRIES) {
      if (retryCount === 0) {
        log("SDK not loaded yet, waiting for script to load...", "kameleoon")
      }
      setTimeout(() => {
        initKameleoon(retryCount + 1)
      }, RETRY_DELAY)
      return
    } else {
      log("SDK failed to load after maximum retries", "kameleoon")
      return
    }
  }

  // Log successful load if we had to retry
  if (retryCount > 0) {
    log(`SDK loaded after ${retryCount} retries`, "kameleoon")
  }

  log("initializing", "kameleoon")

  try {
    // Enable legal consent - allows Kameleoon to collect data (GDPR compliance)
    if (window.Kameleoon.API.Core.enableLegalConsent) {
      window.Kameleoon.API.Core.enableLegalConsent()
      log("legal consent enabled", "kameleoon")
    }

    // Enable SPA support for proper page view tracking on route changes
    // This is required for SPA applications (recommended by Kameleoon docs)
    window.Kameleoon.API.Core.enableSinglePageSupport()
    log("SPA support enabled", "kameleoon")

    kameleoonInitialized = true
    log("ready", "kameleoon")

    // Send initial page_view event after successful initialization
    // Subsequent page_view events are triggered by onRouteUpdate in gatsby-browser.js
    window.Kameleoon.API.Events.trigger("page_view")
    log("page_view event triggered (initial)", "kameleoon")
  } catch (error) {
    log(`initialization failed: ${error}`, "kameleoon")
    console.error("Kameleoon initialization error:", error)
  }
}

/**
 * Disables Kameleoon SDK when user revokes consent
 *
 * Called by CMP when user withdraws analytics consent.
 * Stops all data collection and tracking to comply with GDPR.
 */
export function disableKameleoon(): void {
  // If not initialized, nothing to disable
  if (!kameleoonInitialized) {
    log("not initialized, nothing to disable", "kameleoon")
    return
  }

  // If SDK not loaded, just reset flag
  if (!window.Kameleoon?.API?.Core?.disableLegalConsent) {
    kameleoonInitialized = false
    log("disabled (SDK not available)", "kameleoon")
    return
  }

  log("disabling", "kameleoon")

  try {
    // Disable legal consent - stops all data collection
    window.Kameleoon.API.Core.disableLegalConsent()
    log("legal consent disabled", "kameleoon")

    // Reset initialization flag to prevent page_view events
    kameleoonInitialized = false
    log("disabled", "kameleoon")
  } catch (error) {
    log(`disable failed: ${error}`, "kameleoon")
    console.error("Kameleoon disable error:", error)
    // Always reset flag even if API call fails
    kameleoonInitialized = false
  }
}
